import React, { useState } from 'react';
import { Search, Scroll, Clock, Sparkles, Plus, Presentation } from 'lucide-react';
import { useInventory } from '../hooks/useInventory';
import { SpellButton } from '../components/SpellButton';
import { PresentationMode } from './PresentationMode';
interface DashboardProps {
  onNavigate: (page: string) => void;
  onMuffliato: () => void;
  onGenerateTag: () => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    getStats,
    getShoppingList,
    getExpiringSoon
  } = useInventory();
  const [showPresentation, setShowPresentation] = useState(false);
  const stats = getStats;
  const shoppingList = getShoppingList();
  const expiring = getExpiringSoon();
  if (showPresentation) {
    return <PresentationMode onClose={() => setShowPresentation(false)} data-id="element-151" />;
  }
  return <div className="pb-24 max-w-md mx-auto p-4 min-h-screen" data-id="element-152">
      {/* Header */}
      <header className="flex justify-between items-center mb-8 pt-4" data-id="element-153">
        <div data-id="element-154">
          <h1 className="text-2xl font-cinzel font-bold text-[#2C2C2C] tracking-tight" data-id="element-155">
            The Household Ledger
          </h1>
          <p className="text-sm text-[#722F37] font-serif italic" data-id="element-156">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-157">
          <button onClick={() => setShowPresentation(true)} className="flex items-center gap-2 px-4 py-2 bg-[#722F37] text-[#F5F0E1] rounded-lg border-2 border-[#C9A227] hover:bg-[#8B3A44] transition-colors shadow-md" title="Start Presentation" data-id="element-158">
            <Presentation className="w-4 h-4" data-id="element-159" />
            <span className="font-cinzel font-bold text-sm" data-id="element-160">Present</span>
          </button>
          <button onClick={() => onNavigate('add')} className="w-12 h-12 bg-[#722F37] text-[#F5F0E1] rounded-full flex items-center justify-center shadow-[0_4px_12px_rgba(114,47,55,0.3)] active:scale-95 transition-all border-2 border-[#C9A227] hover:bg-[#8B3A44]" data-id="element-161">
            <Plus className="w-6 h-6" data-id="element-162" />
          </button>
        </div>
      </header>

      {/* Magical Stats */}
      <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-163">
        <div className="bg-[#FFFDF5] rounded-xl p-3 border border-[#C9A227] text-center relative overflow-hidden group" data-id="element-164">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#C9A227]/20" data-id="element-165" />
          <div className="text-2xl font-cinzel font-bold text-[#2C2C2C] group-hover:scale-110 transition-transform" data-id="element-166">
            {stats.totalItems}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#722F37] mt-1" data-id="element-167">
            Total Items
          </div>
        </div>

        <div className={`bg-[#FFFDF5] rounded-xl p-3 border text-center relative overflow-hidden group ${stats.lowStock > 0 ? 'border-[#D4A017]' : 'border-[#C9A227]'}`} data-id="element-168">
          <div className={`absolute top-0 left-0 w-full h-1 ${stats.lowStock > 0 ? 'bg-[#D4A017]' : 'bg-[#C9A227]/20'}`} data-id="element-169" />
          <div className={`text-2xl font-cinzel font-bold group-hover:scale-110 transition-transform ${stats.lowStock > 0 ? 'text-[#D4A017]' : 'text-[#2C2C2C]'}`} data-id="element-170">
            {stats.lowStock}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#722F37] mt-1" data-id="element-171">
            Depleting
          </div>
        </div>

        <div className={`bg-[#FFFDF5] rounded-xl p-3 border text-center relative overflow-hidden group ${stats.expiringSoon > 0 ? 'border-[#8B0000]' : 'border-[#C9A227]'}`} data-id="element-172">
          <div className={`absolute top-0 left-0 w-full h-1 ${stats.expiringSoon > 0 ? 'bg-[#8B0000]' : 'bg-[#C9A227]/20'}`} data-id="element-173" />
          <div className={`text-2xl font-cinzel font-bold group-hover:scale-110 transition-transform ${stats.expiringSoon > 0 ? 'text-[#8B0000]' : 'text-[#2C2C2C]'}`} data-id="element-174">
            {stats.expiringSoon}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#722F37] mt-1" data-id="element-175">
            Tempus
          </div>
        </div>
      </div>

      {/* Spell Actions */}
      <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-176">
        <SpellButton spellName="Accio" description="Summon Items" icon={Search} onClick={() => onNavigate('search')} variant="primary" className="h-32" data-id="element-177" />
        <SpellButton spellName="Revelio" description="Reveal Zones" icon={Sparkles} onClick={() => onNavigate('zones')} variant="secondary" className="h-32" data-id="element-178" />
      </div>

      {/* Requisition Scroll (Shopping List) */}
      <div className="relative bg-[#722F37] rounded-xl p-6 mb-6 text-[#F5F0E1] shadow-lg overflow-hidden border-2 border-[#C9A227]" data-id="element-179">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#C9A227] to-transparent pointer-events-none" data-id="element-180" />

        <div className="relative z-10" data-id="element-181">
          <div className="flex items-center justify-between mb-4" data-id="element-182">
            <div className="flex items-center gap-3" data-id="element-183">
              <div className="w-10 h-10 bg-[#F5F0E1]/10 rounded-full flex items-center justify-center border border-[#C9A227]/30" data-id="element-184">
                <Scroll className="w-5 h-5 text-[#C9A227]" data-id="element-185" />
              </div>
              <div data-id="element-186">
                <h2 className="font-cinzel font-bold text-lg text-[#F5F0E1]" data-id="element-187">
                  Requisition Scroll
                </h2>
                <p className="text-[#C9A227] text-xs font-serif italic" data-id="element-188">
                  {shoppingList.length} items required
                </p>
              </div>
            </div>
          </div>

          {shoppingList.length > 0 ? <>
              <div className="space-y-2 mb-5" data-id="element-189">
                {shoppingList.slice(0, 3).map(item => <div key={item.id} className="flex items-center justify-between bg-[#F5F0E1]/10 rounded-lg px-3 py-2 border border-[#F5F0E1]/5" data-id="element-190">
                    <span className="text-sm font-medium font-serif tracking-wide" data-id="element-191">
                      {item.name}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${item.priority === 'High' ? 'bg-[#8B0000]/40 border-[#8B0000] text-[#FFB3B3]' : item.priority === 'Medium' ? 'bg-[#D4A017]/20 border-[#D4A017] text-[#FFE5A0]' : 'bg-[#2D5A3D]/20 border-[#2D5A3D] text-[#A0E5B3]'}`} data-id="element-192">
                      {item.priority}
                    </span>
                  </div>)}
              </div>
              <button onClick={() => onNavigate('pull-list')} className="w-full py-3 bg-[#F5F0E1] text-[#722F37] rounded-lg font-cinzel font-bold text-sm active:bg-[#E6DCC3] transition-colors border border-[#C9A227]" data-id="element-193">
                Unfurl Scroll
              </button>
            </> : <div className="text-center py-4 text-[#F5F0E1]/70 font-serif italic" data-id="element-194">
              <p data-id="element-195">The stores are plentiful.</p>
            </div>}
        </div>
      </div>

      {/* Tempus Warning (Expiring) */}
      {expiring.length > 0 && <div className="bg-[#FFFDF5] rounded-xl p-5 border border-[#8B0000] relative overflow-hidden" data-id="element-196">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#8B0000]" data-id="element-197" />

          <div className="flex items-center justify-between mb-4 pl-2" data-id="element-198">
            <div className="flex items-center gap-2" data-id="element-199">
              <Clock className="w-5 h-5 text-[#8B0000]" data-id="element-200" />
              <h3 className="font-cinzel font-bold text-[#2C2C2C]" data-id="element-201">
                Tempus Warnings
              </h3>
            </div>
            <button onClick={() => onNavigate('expiring')} className="text-xs text-[#8B0000] font-bold uppercase tracking-wider hover:underline" data-id="element-202">
              Reveal All
            </button>
          </div>

          <div className="space-y-3 pl-2" data-id="element-203">
            {expiring.slice(0, 2).map(item => {
          const days = Math.ceil((new Date(item.expiryDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
          return <div key={item.id} className="flex items-center justify-between py-2 border-b border-[#C9A227]/20 last:border-0" data-id="element-204">
                  <span className="text-sm text-[#2C2C2C] font-serif" data-id="element-205">
                    {item.name}
                  </span>
                  <span className={`text-xs font-bold ${days <= 3 ? 'text-[#8B0000]' : 'text-[#D4A017]'}`} data-id="element-206">
                    {days <= 0 ? 'Expired!' : `${days} days left`}
                  </span>
                </div>;
        })}
          </div>
        </div>}
    </div>;
}